import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { NotificationsApiResponse } from '@novelizing/types';

// Fetch notifications function for react-query
const fetchNotifications = async (
  userId?: number,
): Promise<NotificationsApiResponse[]> => {
  if (!userId) {
    return [];
  }
  const notificationsUrl =
    import.meta.env.VITE_NOTIFICATIONS_URL +
    `/notifications/user/${userId}/unread`;
  const response = await fetch(notificationsUrl, {
    credentials: 'include', // This will include cookies (including JWT) in the request
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch notifications: ${response.status}`);
  }

  // Parse the response as an array of ServerNotification objects
  return (await response.json()) as NotificationsApiResponse[];
};

export interface Notification extends NotificationsApiResponse {
  // Ensure local client-side properties like 'seen' can be added if needed,
  // though backend now handles 'isViewed'
}

export function useNotifications(isOpen: boolean, userId?: number) {
  const queryClient = useQueryClient();

  // Use react-query to fetch notifications only when dropdown is open
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
    isSuccess: notificationsQuerySuccess,
  } = useQuery({
    queryKey: ['notifications', userId],
    queryFn: () => fetchNotifications(userId),
    enabled: isOpen && !!userId, // Only fetch when dropdown is open and userId is available
    staleTime: 1000 * 60, // Consider data stale after 1 minute
    refetchOnWindowFocus: false,
  });

  // Function to mark a notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      // Update the server to mark notification as read
      const notificationsUrl = import.meta.env.VITE_NOTIFICATIONS_URL;
      await fetch(`${notificationsUrl}/notifications/${id}/read`, {
        method: 'POST',
        credentials: 'include', // Include JWT cookie
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isRead: true }),
      });
      return id;
    },
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notifications', userId] });

      // Snapshot the previous value
      const previousNotifications = queryClient.getQueryData([
        'notifications',
        userId,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ['notifications', userId],
        (old: Notification[] | undefined) =>
          (old || []).map((notification) =>
            notification.id === id
              ? { ...notification, read: true }
              : notification,
          ),
      );

      // Return a context object with the snapshot
      return { previousNotifications };
    },
    onError: (err, _, context) => {
      console.error('Error marking notification as read:', err);
      // If there was an error, roll back to the previous value
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['notifications', userId],
          context.previousNotifications,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data is in sync
      // queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Function to mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      // Update the server to mark all notifications as read
      const notificationsUrl = import.meta.env.VITE_NOTIFICATIONS_URL;
      await fetch(`${notificationsUrl}/notifications/user/${userId}/read-all`, {
        method: 'POST',
        credentials: 'include', // Include JWT cookie
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isRead: true }),
      });
      return true;
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notifications', userId] });

      // Snapshot the previous value
      const previousNotifications = queryClient.getQueryData([
        'notifications',
        userId,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ['notifications', userId],
        (old: Notification[] | undefined) =>
          (old || []).map((notification) => ({
            ...notification,
            isRead: true,
            read: true,
          })),
      );

      // Return a context object with the snapshot
      return { previousNotifications };
    },
    onError: (err, _, context) => {
      console.error('Error marking all notifications as read:', err);
      // If there was an error, roll back to the previous value
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['notifications', userId],
          context.previousNotifications,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data is in sync
      // queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAsRead = (id: string) => {
    markAsReadMutation.mutate(id);
  };

  const markAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  // Function to mark all notifications as seen (viewed)
  const markAllAsSeenMutation = useMutation({
    mutationFn: async () => {
      const notificationsUrl = import.meta.env.VITE_NOTIFICATIONS_URL;
      await fetch(
        `${notificationsUrl}/notifications/user/${userId}/viewed-all`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return true;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications', userId] });
      const previousNotifications = queryClient.getQueryData([
        'notifications',
        userId,
      ]);
      queryClient.setQueryData(
        ['notifications', userId],
        (old: Notification[] | undefined) =>
          (old || []).map((notification) => ({
            ...notification,
            isViewed: true,
            seen: true,
          })),
      );
      return { previousNotifications };
    },
    onError: (err, _, context) => {
      console.error('Error marking all notifications as seen:', err);
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['notifications', userId],
          context.previousNotifications,
        );
      }
    },
    onSettled: () => {
      // queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
    },
  });

  const markAllAsSeen = () => {
    markAllAsSeenMutation.mutate();
  };

  // Placeholder for individual markAsSeen - backend controller does not have this yet
  // For now, it will be a client-side optimistic update if used.
  const markAsSeen = (id: string) => {
    queryClient.setQueryData(
      ['notifications', userId],
      (old: Notification[] | undefined) =>
        (old || []).map((notification) =>
          notification.id === id
            ? { ...notification, isViewed: true, seen: true } // Optimistically mark as seen
            : notification,
        ),
    );
    // TODO: If a backend endpoint for individual markAsSeen is added, call mutation here.
    console.warn('Individual markAsSeen is client-side only for now.');
  };

  return {
    notifications,
    loading: isLoading,
    error,
    refetchNotifications: refetch,
    notificationsQuerySuccess,
    markAsRead, // Individual mark as read
    markAllAsRead, // All as read
    markAsSeen, // Individual mark as seen (currently client-side only)
    markAllAsSeen, // All as seen (viewed)
  };
}
